// Local blog posts, migrated from WordPress. Each post lives in
// src/content/posts/<slug>.meta.json (metadata) and <slug>.html (body).
// The metadata is eagerly bundled (small, ~5KB total); the HTML body is
// lazy-loaded per post to keep it out of the shared client bundle.

export type PostMeta = {
  id: number;
  slug: string;
  title: string;
  subtitle?: string;
  seoTitle?: string;
  seoDescription?: string;
  excerpt: string;
  date: string;
  featuredImage: string | null;
  /** Optimized WebP thumbnail used in post listings. */
  featuredImageThumb?: string;
  /** srcset for the optimized listing thumbnail. */
  featuredImageThumbSrcSet?: string;
  /** Alt text for the featured image. Required whenever featuredImage is set. */
  featuredImageAlt?: string;
  /** Optional caption/credit rendered under the featured image. */
  featuredImageCaption?: string;
  /** Optional ImageObject metadata for figures inside the post body. */
  images?: {
    url: string;
    name?: string;
    caption?: string;
    description?: string;
    width?: number;
    height?: number;
  }[];
};

export type Post = PostMeta & {
  contentHtml: string;
};

const metaModules = import.meta.glob<PostMeta>(
  "../content/posts/*.meta.json",
  { eager: true, import: "default" },
);

const htmlModules = import.meta.glob<string>("../content/posts/*.html", {
  query: "?raw",
  import: "default",
});

function fileBase(path: string, suffix: string): string {
  const name = path.slice(path.lastIndexOf("/") + 1);
  return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
}

// Map url-slug -> html loader, keyed by the meta filename base so the two
// files stay paired even when the meta's `slug` field differs from the
// filename (e.g. renamed posts).
const htmlLoaderBySlug: Record<string, () => Promise<string>> = {};
for (const [metaPath, meta] of Object.entries(metaModules)) {
  const base = fileBase(metaPath, ".meta.json");
  const htmlPath = Object.keys(htmlModules).find(
    (k) => fileBase(k, ".html") === base,
  );
  if (htmlPath) htmlLoaderBySlug[meta.slug] = htmlModules[htmlPath];
}

const allMeta: PostMeta[] = Object.values(metaModules).sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
);

const metaBySlug: Record<string, PostMeta> = Object.fromEntries(
  allMeta.map((p) => [p.slug, p]),
);

export function listPosts(): PostMeta[] {
  return allMeta;
}

export function getPostMetaBySlug(slug: string): PostMeta | null {
  return metaBySlug[slug] ?? null;
}

// Return the newer (prev) and older (next) post relative to `slug`, using the
// same date-desc order as listPosts(). Used to prefetch adjacent article HTML.
export function getAdjacentPosts(slug: string): {
  prev: PostMeta | null;
  next: PostMeta | null;
} {
  const idx = allMeta.findIndex((p) => p.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? allMeta[idx - 1] : null,
    next: idx < allMeta.length - 1 ? allMeta[idx + 1] : null,
  };
}

// Warm the html cache for a slug without awaiting — used for adjacent-post
// prefetch. Safe to call repeatedly; getPostBySlug reuses the same promise.
export function prefetchPostHtml(slug: string): void {
  if (htmlCache.has(slug)) return;
  const loader = htmlLoaderBySlug[slug];
  if (!loader) return;
  const p = loader().catch((err) => {
    htmlCache.delete(slug);
    throw err;
  });
  htmlCache.set(slug, p);
}

// Cache the resolved HTML per slug so repeat views (client re-mounts, SSR
// re-requests within the same worker instance) reuse the already-loaded
// module instead of re-invoking the dynamic import.
const htmlCache = new Map<string, Promise<string>>();

export async function getPostBySlug(slug: string): Promise<Post | null> {
  const meta = metaBySlug[slug];
  if (!meta) return null;
  let htmlPromise = htmlCache.get(slug);
  if (!htmlPromise) {
    const loader = htmlLoaderBySlug[slug];
    if (!loader) return null;
    htmlPromise = loader().catch((err) => {
      htmlCache.delete(slug); // don't cache failures
      throw err;
    });
    htmlCache.set(slug, htmlPromise);
  }
  const contentHtml = await htmlPromise;
  return { ...meta, contentHtml };
}


